import { type ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface TextColumnOptions<T> {
  /**
   * Unique identifier (falls back to accessorKey if omitted)
   *
   * @return {string} unique identifier
   */
  id?: string;

  accessorKey?: keyof T;
  accessorFn?: (row: T) => any;

  /**
   * Column title
   *
   * @return {string} column title string
   */
  label: string;

  /**
   * Gallery view (LIST or TILE)
   *
   * @return {GalleryView}
   */
  galleryView?: GalleryView;

  /**
   * isMainColumn property changes font's color and width to be more prominent
   *
   * @return {boolean}
   */
  isMainColumn?: boolean;

  /**
   * Let this column absorb leftover space in the fixed-layout table instead of
   * a locked width. isMainColumn already implies this; set explicitly to flex
   * a non-main column (e.g. slug/description growing alongside the name).
   *
   * @return {boolean}
   */
  flexWidth?: boolean;

  /**
   * Clamp vs wrap the cell’s content
   *
   * @return {boolean}
   */
  truncateValue?: boolean;

  /**
   * Show header with clamp + tooltip
   *
   * @return {boolean}
   */
  truncateHeaderValue?: boolean;

  /** Override for your main column in TILE (only if truncateValue)
   *
   * @return {string}
   */
  headerClassName?: string;

  /** Override your cell styles
   *
   * @return {string}
   */
  cellClassName?: string;

  /** Disable if you don't want the column to be hideable
   *
   * @return {boolean}
   */
  enableHiding?: boolean;

  /** Disable if you don't want the column to be sortable
   *
   * @return {boolean}
   */
  enableSorting?: boolean;

  /** Min width of the column
   *
   * @return {number}
   */
  minSize?: number;

  /** Max width of the column
   *
   * @return {number}
   */
  maxSize?: number;

  /** Width of the column
   *
   * @return {number}
   */
  size?: number;

  /** Data-cy attribute for testing
   *
   * @return {string}
   */
  dataCy?: string;
}

export const TextColumn = <T extends Record<string, any>>(opts: TextColumnOptions<T>): ColumnDef<T> => {
  const {
    id,
    accessorFn,
    accessorKey,
    label,
    galleryView,
    size,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    isMainColumn = false,
    flexWidth = false,
    truncateValue = true,
    truncateHeaderValue = false,
    minSize,
    maxSize,
    dataCy,
  } = opts;

  return {
    id,
    ...(accessorKey ? { accessorKey: String(accessorKey) } : {}),
    ...(accessorFn ? { accessorFn } : {}),
    enableSorting,
    size,
    minSize,
    maxSize,
    header: () =>
      truncateHeaderValue ? (
        <Tooltip>
          <TooltipTrigger className={cn({ 'line-clamp-1': truncateHeaderValue })}>{label}</TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-44 break-all capitalize">{label}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : (
        label
      ),
    meta: {
      // The main column flexes: no fixed width in the list, absorbs leftover space
      flexWidth: flexWidth || isMainColumn,
      cellClassName: cn(
        {
          'text-left dark:text-white text-black font-semibold': isMainColumn,
          // Cap the list-view <td> width so an auto-layout table can't grow it to the
          // content's width. Without this, the inner line-clamp never clips and long
          // unbroken names blow out the row instead of truncating. (DM-2393)
          'max-w-0': truncateValue,
        },
        // Passed last so a caller-supplied max-w-* (e.g. a bounded width under
        // intrinsicTableWidth tables, which have no fixed column width to clip
        // against) overrides the max-w-0 default above.
        cellClassName,
      ),
      ...(headerClassName && { headerClassName: headerClassName }),
    },
    enableHiding,
    cell: ({ getValue, row }) => {
      const raw = getValue();

      if (raw === null) {
        return '';
      }

      const text = String(raw);

      const isTileView = galleryView === GalleryView.TILE;

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <p
              className={cn({
                // -webkit-line-clamp doesn't reliably shrink to 0 min-content like a plain
                // block does, so an unbroken name still blew out the row. `truncate` (nowrap +
                // overflow-hidden + ellipsis) has no wrapping to compute and always clips. (DM-2393)
                'truncate': truncateValue,
                'break-all': !truncateValue,

                // override for your main column in TILE (only if truncateValue). min-w-0 lets
                // this grow flex item shrink below its content width so truncate can clip —
                // without it a long unbroken name overflows the tile row instead. (DM-2393)
                'truncate grow min-w-0 text-sm font-medium text-text-base': isMainColumn && isTileView && truncateValue,

                // non-main columns always get gray
                'text-text-gray': !isMainColumn,
                // and they shrink in TILE
                'grow min-w-0 text-xs': !isMainColumn && isTileView,
              })}
              data-cy={dataCy ?? `${galleryView ?? 'list'}-${row.index}-${id?.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {accessorKey === 'count' && isTileView ? `Share count: ${text}` : text}
            </p>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-44 break-all">{text}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      );
    },
  } as ColumnDef<T>;
};
